import { useEffect, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Download, Smartphone, CheckCircle2 } from "lucide-react";
import { onInstallAvailable, promptInstall, isStandalone } from "../../lib/pwaInstall";

/* Permanent "Get the TravelerPulse app" card on the home page.
   Unlike the floating banner, this never dismisses — hidden only when the
   site is already running as the installed app. */
export const GetAppCard = () => {
  const [canPrompt, setCanPrompt] = useState(false);
  useEffect(() => onInstallAvailable(setCanPrompt), []);
  if (isStandalone()) return null;

  const appLink = window.location.origin;
  const waHref = `https://wa.me/?text=${encodeURIComponent(
    `Install the TravelerPulse app \u{1F4F1} Open this link on your phone, then tap Share \u2192 "Add to Home Screen":\n${appLink}`
  )}`;
  const install = async () => {
    if (canPrompt) { await promptInstall(); return; }
    window.dispatchEvent(new CustomEvent("tp:install-guide"));
  };

  return (
    <div className="mt-6 bg-card border border-border rounded-2xl p-5 sm:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-5 shadow-warm-sm" data-testid="home-getapp-card">
      <img src="/pwa-icon-192.png" alt="My Social Pulse app" className="w-12 h-12 rounded-2xl shrink-0 shadow-sm" />
      <div className="flex-1 min-w-0">
        <h3 className="font-serif font-800 text-lg tracking-tight text-primary flex items-center gap-2">
          <Smartphone className="w-4 h-4" strokeWidth={1.75} /> Get the My Social Pulse app
        </h3>
        <p className="text-sm text-muted-foreground mt-0.5">Free — installs in seconds, no app store needed.</p>
        <ul className="mt-2 flex flex-wrap gap-x-4 gap-y-1">
          {["Works offline on trips", "Flight & reminder alerts", "Lives on your home screen"].map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" strokeWidth={2} /> {t}
            </li>
          ))}
        </ul>
        <div className="mt-3 flex flex-wrap items-center gap-2.5">
          <button onClick={install} data-testid="home-getapp-install-btn"
            className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground px-4 py-2 text-sm font-700 rounded-full hover:opacity-95 transition-opacity">
            <Download className="w-4 h-4" strokeWidth={2} /> {canPrompt ? "Install now" : "Show me how"}
          </button>
          <a href={waHref} target="_blank" rel="noreferrer" data-testid="home-getapp-whatsapp"
            className="inline-flex items-center gap-1.5 border border-border text-foreground px-4 py-2 text-xs font-700 rounded-full hover:border-primary hover:text-primary transition-colors">
            Send link to my phone
          </a>
        </div>
      </div>
      <div className="hidden md:flex items-center gap-3 shrink-0 bg-secondary/60 rounded-xl p-3 border border-border" data-testid="home-getapp-qr">
        <QRCodeSVG value={appLink} size={72} level="M" includeMargin={false} />
        <span className="text-[11px] text-muted-foreground max-w-[110px] leading-snug">Scan with your phone camera to install</span>
      </div>
    </div>
  );
};
